/* eslint-disable @typescript-eslint/no-unused-vars */
import clsx from 'clsx';
import { Icon } from 'common/icon';

import { InputField } from 'common';
import { Button } from 'common/new-button';
import { useAlert } from 'components';
import { ICasino, ISlot } from 'constants/types';
import { Formik, FormikValues } from 'formik';
import Image from 'next/image';
import { initialValues, model, validationSchema } from './config';

import { Dropdown, DropdownRef } from 'components/dropdown/slots';
import { DropdownItem } from 'components/dropdown/slots/item';

import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { useClickOutside } from 'hooks/useClickOutside';
import { useCallback, useMemo, useRef, useState, MouseEvent } from 'react';
import { useCreateSlotReviewMutation } from 'hooks/api/slot_reviews';
import { useSlotsByCasinoIdQuery } from 'hooks/api/slots';
import { useMeQuery } from 'hooks/api/users';
import { handleMutation } from 'utils/handleMutation';

import { SelectArrow } from 'components/modal-arrow';
import styles from './styles.module.css';
import { crocodileSearch } from 'utils/search';

type Props = {
  casino?: ICasino;
  isVisible: boolean;
  setVisible: (isVisible: boolean) => void;
};

const SlotButton = ({
  isOpen,
  onClick,
  disabled,
  placeholder,
  searchQuery,
  selectedSlot,
  setSearchQuery,
}: {
  isOpen?: boolean;
  disabled?: boolean;
  placeholder?: string;
  searchQuery: string;
  onClick?: () => void;
  selectedSlot: ISlot | null;
  setSearchQuery: (query: string) => void;
}) => {
  const alert = useAlert();

  const handleButtonClick = (e: MouseEvent) => {
    if (disabled) {
      e.stopPropagation();
      alert.error('Please wait for the slots to load');
      return;
    }

    if (onClick) {
      onClick();
    }
  };

  if (isOpen) {
    return (
      <div
        className={clsx(styles.slot_row_button, {
          [styles.disabled]: disabled,
        })}
        onClick={handleButtonClick}
      >
        <input
          autoFocus
          disabled={disabled}
          value={searchQuery}
          placeholder={placeholder ?? 'Search slot'}
          className={styles.slot_search_input}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <SelectArrow isOpen={isOpen} />
      </div>
    );
  }

  if (!selectedSlot) {
    return (
      <div
        className={clsx(styles.slot_row_button, {
          [styles.disabled]: disabled,
        })}
        onClick={handleButtonClick}
      >
        <span className={styles.slot_name}>{placeholder ?? 'Select slot'}</span>
        <SelectArrow isOpen={isOpen} />
      </div>
    );
  }

  return (
    <div className={styles.slot_row_button} onClick={handleButtonClick}>
      <Image
        src={selectedSlot?.imageUrl}
        width={20}
        height={20}
        alt={selectedSlot?.name}
      />
      <span className={styles.slot_name}>{selectedSlot?.name}</span>

      <SelectArrow isOpen={isOpen} />
    </div>
  );
};

export const CasinoSlotReviewModal = ({
  isVisible,
  setVisible,
  casino,
}: Props) => {
  const alert = useAlert();
  const modalRef = useRef<HTMLDivElement | null>(null);

  const { data: me } = useMeQuery();

  const dropdownRef = useRef<DropdownRef | null>(null);

  const { data: { results: slots = [] } = {}, isFetching } =
    useSlotsByCasinoIdQuery(
      {
        id: casino?._id!!,
      },
      { skip: !casino?._id }
    );

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitReview, mutation] = useCreateSlotReviewMutation();
  const isLoading = mutation.isLoading;

  const onSuccess = useCallback(() => {
    alert.success('Review successfully added');
  }, [alert]);

  handleMutation({ ...mutation, onSuccess });

  const data = useMemo(() => {
    if (!searchQuery?.length) return slots;

    return slots?.filter((slot) => {
      return crocodileSearch(slot?.name, searchQuery);
    });
  }, [slots, searchQuery]);

  const handleToggle = () => {
    if (isFetching) return;
    setIsOpen(!isOpen);
  };

  const handleSelectSlot = (
    slot: ISlot,
    setFieldValue: (field: string, value: string) => void
  ) => {
    setFieldValue('slot', JSON.stringify(slot));
    dropdownRef?.current?.close();
    setIsOpen(false);
    setSearchQuery('');
  };

  const onSubmit = async (values: FormikValues) => {
    const { slot: slotString, value, screenshot } = values;
    let slot = {} as ISlot;

    if (!me?.user?._id) {
      alert.error('Please login to leave a review');
      return;
    }

    if (slotString) {
      slot = JSON.parse(slotString);
    } else {
      alert.error('Please select slot');
      return;
    }

    if (Number(value) < 0 || Number(value) > 100) {
      alert.error('Please enter valid RTP value');
      return;
    }

    if (!screenshot) {
      alert.error('Please upload screenshot');
      return;
    }

    await submitReview({
      value,
      image: screenshot,
      slot_id: slot?._id!!,
      casino_id: casino?._id!!,
    });

    setVisible(false);
  };

  const handleCloseModal = () => {
    setVisible(false);
    setIsOpen(false);
    setSearchQuery('');
    dropdownRef.current?.close();
  };

  useClickOutside({ onClick: handleCloseModal, ref: modalRef });

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.modalBody, styles.modalBodyActive)}>
      <div className={styles.container}>
        <Formik
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={false}
        >
          {({
            dirty,
            values,
            isSubmitting,
            submitForm,
            validateForm,
            setFieldValue,
          }) => (
            <div className={styles.wrapper} ref={modalRef}>
              <div className={styles.top}>
                <div className={styles.topLeft}>
                  <div className={styles.text}>
                    <span>{'Add slot RTP review'}</span>
                  </div>
                </div>

                <div className={styles.closeButton} onClick={handleCloseModal}>
                  <Icon.Close />
                </div>
              </div>

              <Dropdown
                ref={dropdownRef}
                button={
                  <SlotButton
                    disabled={isFetching}
                    isOpen={isOpen}
                    placeholder={isFetching ? 'Searching slots...' : undefined}
                    onClick={handleToggle}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    selectedSlot={
                      values?.slot ? (JSON.parse(values?.slot) as ISlot) : null
                    }
                  />
                }
              >
                {data?.map((slot) => (
                  <DropdownItem
                    key={slot?._id}
                    onClick={() => handleSelectSlot(slot, setFieldValue)}
                  >
                    <div className={styles.slot_row}>
                      <div className={styles.wrapper_name}>
                        <LogoOverlayWrapper size={20}>
                          <LogoOverlay
                            width={20}
                            height={20}
                            alt={casino?.name}
                            image={slot?.logoUrl}
                          />
                          <Image
                            src={slot.imageUrl}
                            width={20}
                            height={20}
                            alt={slot.name}
                          />
                        </LogoOverlayWrapper>
                        <span className={styles.slot_name}>{slot.name}</span>
                      </div>
                    </div>
                  </DropdownItem>
                ))}
              </Dropdown>

              <div className={styles.wrapperInput}>
                <InputField {...model.value} />
              </div>
              <div className={styles.screenshotInput}>
                <InputField {...model.screenshot} />
                <div className={styles.wrapperScreenshotButton}>
                  <Icon.Cloud />
                  <span>
                    {(values?.screenshot as any)?.name ?? 'Screenshot proof...'}
                  </span>
                </div>
              </div>
              <div className={styles.submitButton}>
                <Button
                  onClick={() => {
                    validateForm();
                    submitForm();
                  }}
                  disabled={!values?.slot || isSubmitting}
                  text={isLoading ? 'Loading...' : 'Send review'}
                />
              </div>
            </div>
          )}
        </Formik>
      </div>
    </div>
  );
};
